import { Form } from 'antd';
import { FC, useState } from 'react';

import { AppMessage, AppModal } from '@/common/components';
import { AppModalControlProps, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import {
  PagesCriticalCssFormFields,
  PagesCriticalCssInputs,
  useEditPageCriticalCssMutation,
  useGeneratePagesCriticalCssMutation,
  useGetPageCriticalCssQuery,
} from '@/modules/Pages';

import { PagesCriticalCssFormView } from './PagesCriticalCssFormView';
import { pageCriticalCssValidationSchema } from './validation';

interface Props {
  modalControls: AppModalControlProps;
  siteId: string;
  pageId: string;
}

export const PagesCriticalCssFormContainer: FC<Props> = ({ modalControls, siteId, pageId }) => {
  const [form] = Form.useForm<PagesCriticalCssFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof PagesCriticalCssInputs>>(null);
  const appMessageApi = AppMessage.useControls();

  const {
    data: pageCriticalCssData,
    isLoading: isPageCriticalCssLoading,
    error: pageCriticalCssError,
    isError: isPageCriticalCssError
  } = useGetPageCriticalCssQuery(pageId, siteId)({
    enabled: !!siteId && !!pageId && modalControls.isOpen,
    onSuccess: (data) => {
      form.setFieldsValue({ css: data.css });
    }
  });

  const { mutate: generatePageCriticalMutation, isLoading: isPageCriticalCssGenerating } = useGeneratePagesCriticalCssMutation(siteId)({
    onSuccess: () => {
      appMessageApi.success('Запит на генерацію Critical CSS сторінки успішно відправлено!');
      handleCloseModal();
    },
    onError: (err) => messageOnError(err, appMessageApi),
  });

  const { mutate: editPageCriticalCssMutation, isLoading: isEditPageCriticalCssLoading } = useEditPageCriticalCssMutation(pageId, siteId)({
    onSuccess: () => {
      appMessageApi.success('Critical CSS сторінки успішно змінено!');
      handleCloseModal();
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof PagesCriticalCssInputs>(err));
      messageOnError(err, appMessageApi);
    },
  });

  const initialValues: PagesCriticalCssFormFields = {
    css: pageCriticalCssData?.css ?? ''
  };

  const handleFinish = (values: PagesCriticalCssFormFields) => {
    setFormErrors(null);
    editPageCriticalCssMutation({ css: values.css ?? '' });
  };

  const handleGenerate = () => {
    generatePageCriticalMutation({ pageId });
  };

  const handleFinishFailed = () => setFormErrors(null);

  const handleCloseModal = () => {
    modalControls.close();
    form.resetFields();
  };

  return (
    <AppModal
      title="Переглянути Critical CSS"
      width={1000}
      footer={false}
      maskClosable={false}
      centered
      controls={{ ...modalControls, close: handleCloseModal }}
      styles={{ content: { minHeight: '650px' } }}
    >
      <FallbackedLayout
        data={pageCriticalCssData}
        error={pageCriticalCssError}
        isError={isPageCriticalCssError}
        isLoading={isPageCriticalCssLoading}
      >
        {
          (pageCriticalCss) => (
            <PagesCriticalCssFormView
              formName="page-critical-css-form"
              formType="edit"
              formErrors={formErrors}
              form={form}
              initialValues={initialValues}
              validationSchema={pageCriticalCssValidationSchema}
              handleBack={handleCloseModal}
              handleFinish={handleFinish}
              handleFinishFailed={handleFinishFailed}
              isFormLoading={isEditPageCriticalCssLoading || isPageCriticalCssGenerating || isPageCriticalCssLoading}
              pageCriticalCss={pageCriticalCss}
              isPageCriticalCssLoading={isPageCriticalCssLoading}
              handleGenerate={handleGenerate}
            />
          )
        }
      </FallbackedLayout>
    </AppModal>
  );
};
